import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchCustomCss } from '../utils/cssUploadUtils';
import { get, post, put } from 'aws-amplify/api';
import { useParams } from 'react-router-dom';
import { showAlert } from '../utils/alerts';
import { safeHttpUrl } from '../utils/linkValidation';
import LoadingSpinner from '../components/LoadingSpinner';
import SavingIndicator from '../components/SavingIndicator';
import { flushSync } from 'react-dom';
import { createRequestQueue } from '../utils/requestQueue';
import debounce from 'lodash/debounce';
import '../styles/base.css';
import '../styles/invitation.css';

// Render user-provided text with newlines converted to line breaks, without
// using dangerouslySetInnerHTML. React escapes the content, so any HTML in the
// stored event fields is shown as plain text instead of executing (prevents
// stored XSS on this public invitation page).
const renderMultilineText = (text) => {
  if (!text) return null;
  return String(text).split('\n').map((line, index, lines) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

function OpenInvitationScreen() {
  const { code } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestResponses, setGuestResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [buttonStates, setButtonStates] = useState({});
  const [guestInputs, setGuestInputs] = useState({});
  const [hasChanges, setHasChanges] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const savingStateTimeouts = useRef({});
  const buttonStateTimestamps = useRef({});
  const requestQueue = useRef(createRequestQueue());
  const updateTimeouts = useRef({});
  const lastConfirmedValues = useRef({});


  // Track field changes
  const trackFieldChange = useCallback((guestId, fieldName, value) => {
    const lastValue = lastConfirmedValues.current[guestId]?.[fieldName];
    if (value !== lastValue) {
      setChangedFields(prev => ({
        ...prev,
        [guestId]: {
          ...prev[guestId],
          [fieldName]: true
        }
      }));
    }
  }, []);

  const setSavingState = useCallback((guestId, field, state) => {
    // Clear any existing timeout for this field
    if (savingStateTimeouts.current[`${guestId}-${field}`]) {
      clearTimeout(savingStateTimeouts.current[`${guestId}-${field}`]);
    }

    setSavingStates(prev => ({
      ...prev,
      [guestId]: {
        ...prev[guestId],
        [field]: state
      }
    }));

    // If state is 'success', set timeout to clear it
    if (state === 'success') {
      savingStateTimeouts.current[`${guestId}-${field}`] = setTimeout(() => {
        setSavingStates(prev => ({
          ...prev,
          [guestId]: {
            ...prev[guestId],
            [field]: null
          }
        }));
      }, 3000);
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(savingStateTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState(null);
  const [customCssLoaded, setCustomCssLoaded] = useState(false);
  const [rsvpCutoffPassed, setRsvpCutoffPassed] = useState(false);

  const loadCustomCss = useCallback(async () => {
    if (event?.customCss && !customCssLoaded) {
      // Guard against CSS injection: customCss is interpolated into a
      // url("...") string in the @import rules below. Breaking out of that
      // string literal to inject arbitrary CSS (defacement / data
      // exfiltration) requires a closing quote or parenthesis; reject those
      // plus backslashes, angle brackets, backticks and whitespace.
      // Semicolons, colons, ampersands etc. are allowed: inside the quoted
      // url() they are inert, and they appear in legitimate URLs such as
      // Google Fonts (family=Roboto:wght@400;700).
      if (/["'()\\\s<>`]/.test(String(event.customCss))) {
        return;
      }
      if (event.customCss.startsWith('http://') || event.customCss.startsWith('https://') || event.customCss.startsWith('//')) {
        // For external URLs, use @import rule
        const styleElement = document.createElement('style');
        styleElement.textContent = `@import url("${event.customCss}");`;
        document.head.appendChild(styleElement);
        setCustomCssLoaded(true);        
      }
      else if (event.customCss.startsWith('/')) {
        // TODO lockdown startswith to /assets or whatever path is finalised
        // For static relative URLs to /assets, use @import rule
        const styleElement = document.createElement('style');
        styleElement.textContent = `@import url("${event.customCss}");`;
        document.head.appendChild(styleElement);
        setCustomCssLoaded(true);
      }
      else {
        // For local files, use the fetched CSS content directly
        // TODO replace this with cf rewrite to s3/uuid/file
        const cssContent = await fetchCustomCss(event.customCss);
        if (cssContent) {
          const styleElement = document.createElement('style');
          styleElement.textContent = cssContent;
          document.head.appendChild(styleElement);
          setCustomCssLoaded(true);
        }
      }
    }
  }, [event, customCssLoaded]);

  useEffect(() => {
    loadCustomCss();
  }, [loadCustomCss]);

  const fetchInvitation = useCallback(async () => {
    try {
      const token = localStorage.getItem(`invitation_token_${code}`);
      const headers = token ? { Authorization: token } : {};
      
      console.log('Fetching invitation with code:', code);
      const restOperation = get({
        apiName: 'events',
        path: `/invitation/${code}`,
        options: {
          headers
        }
      });

      const { body } = await restOperation.response;
      console.log('Received API response:', restOperation.response);
      if (!body) {
        console.error('Response is missing required body');
        throw new Error('Response is missing required body');
      }
      console.log('Successfully parsed body', body);      

      const data = await body.json();

      if (!data) {
        console.error('Response is missing required json data');
        throw new Error('Response is missing required json data');
      }

      console.log('Successfully parsed invitation data', data);

      if (!data.invitation) {
        console.error('Response is missing invitation data');
        throw new Error('Response is missing invitation data');
      }

      setInvitation(data.invitation);
      setEvent(data.event);
      setRsvpCutoffPassed(data.rsvpCutoffPassed || false);
      // Initialize guest inputs with existing RSVP data
      const initialGuestInputs = {};
      if (data.guests) {
        setGuests(data.guests);
        // Create a map of RSVP data by guest_id for easier lookup
        const rsvpMap = {};
        if (Array.isArray(data.rsvp)) {
          data.rsvp.forEach(rsvp => {
            if (rsvp.guest_id) {
              rsvpMap[rsvp.guest_id] = {
                ...rsvp,
                attending: rsvp.attending === undefined || rsvp.attending === null ? undefined : Boolean(rsvp.attending)
              };
            }
          });
        }
        
        data.guests.forEach(guest => {
          const guestRsvp = rsvpMap[guest.id] || {};
          initialGuestInputs[guest.id] = {
            // Handle attending state properly - can be true, false or undefined
            // TODO consider changing logic to ?? as it ignores falsy values
            attending: guestRsvp.attending === undefined || guestRsvp.attending === null ? undefined : Boolean(guestRsvp.attending),
            dietaryRequirements: guestRsvp.dietaryRequirements || '',
            additionalOptions: guestRsvp.additionalOptions?.toString() || 'false',
            additionalNotes: guestRsvp.additionalNotes || '',
            ageCategory: guestRsvp.ageCategory || '',
            guestComments: guestRsvp.guestComments || ''
          };
        });
        setGuestInputs(initialGuestInputs);
        
        // Also update the guestResponses state with the same data
        setGuestResponses(rsvpMap);
      }
      
      if (token) {
        setIsAuthenticated(true);
        setShowPasswordForm(false);
      }

    } catch (error) {
      console.error('Error fetching invitation:', {
        error: error,
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      });
      if (error.response && error.response.status === 401) {
        setShowPasswordForm(true);
        setIsAuthenticated(false);
        setError('This invitation requires a password to view');
      } else {
        setError(error.message || 'Failed to load invitation. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (Object.keys(guestResponses).length > 0) {
      // Initialize lastConfirmedValues with current guest responses
      lastConfirmedValues.current = Object.entries(guestResponses).reduce((acc, [guestId, response]) => {
        acc[guestId] = {
          ...response,
          // TODO consider changing logic to ?? as it ignores falsy values
          attending: response.attending === true || response.attending === false ? Boolean(response.attending) : undefined,
          dietaryRequirements: response.dietaryRequirements || '',
          additionalOptions: response.additionalOptions?.toString() || 'false',
          additionalNotes: response.additionalNotes || '',
          ageCategory: response.ageCategory || '',
          guestComments: response.guestComments || ''
        };
        return acc;
      }, {});
    }
  }, [guestResponses]);

  useEffect(() => {
    setIsLoading(true);
    fetchInvitation();
  }, [fetchInvitation]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await post({
        apiName: 'events',
        path: `/invitation/${code}/authenticate`,
        options: {
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password })
        }
      });

      const token = data.token;
      if (token) {
        localStorage.setItem(`invitation_token_${code}`, token);
        setIsAuthenticated(true);
        setShowPasswordForm(false);
        await fetchInvitation();
      } else {
        throw new Error('Invalid response from server');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setIsAuthenticated(true);
      setShowPasswordForm(false);
      
      const inviteController = new AbortController();
      await fetchInvitation(inviteController.signal);
      inviteController.abort();
    } catch (error) {
      console.error('Error authenticating:', error);
      setError(error.message || 'Failed to authenticate. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateRSVPs = useCallback(async (updatedGuests) => {
    // Check if RSVP cutoff has passed
    if (rsvpCutoffPassed) {
      setError('RSVP cutoff has passed. No further changes are allowed.');
      return;
    }
    
    console.log('[updateRSVPs] Starting update with guests:', updatedGuests);
    
    // Create a unique update ID for this batch of changes
    const updateId = Date.now().toString();
    console.log('[updateRSVPs] Generated updateId:', updateId);
    
    // Add this update to pending updates
    setPendingUpdates(prev => {
      const newUpdates = {
        ...prev,
        [updateId]: updatedGuests
      };
      console.log('[updateRSVPs] Updated pending updates:', newUpdates);
      return newUpdates;
    });

    try {
      // Queue this update request and get a promise that resolves when it's our turn
      const updatePromise = requestQueue.current.enqueue(async () => {
        // Get the most recent state when executing the request
        const currentPendingUpdates = { ...pendingUpdates };
        const currentGuestResponses = { ...guestResponses };
        console.log('[updateRSVPs] Current pending updates:', currentPendingUpdates);
        console.log('[updateRSVPs] Current guest responses:', currentGuestResponses);
        
        // Merge all pending updates for the affected guests
        const mergedUpdates = { ...updatedGuests };
        console.log('[updateRSVPs] Merged updates:', mergedUpdates);

        // Only send fields that have changed since last confirmed response
        const changedFieldsToSend = {};
        Object.entries(mergedUpdates).forEach(([guestId, updates]) => {
          const lastConfirmed = lastConfirmedValues.current[guestId] || {};
          console.log(`[updateRSVPs] Processing guest ${guestId}:`, {
            updates,
            lastConfirmed: lastConfirmedValues.current[guestId]
          });
          
          const changedFields = {
            guest_id: guestId // Always include guest_id
          };
          let hasChanges = false;

          Object.entries(updates).forEach(([field, value]) => {
            if (field !== 'guest_id') {
              // Compare with lastConfirmed value, considering undefined/null as equal to empty string (using ?? as it ignores falsy values)
              const lastValue = lastConfirmed[field] ?? '';
              const newValue = value ?? '';
              if (lastValue !== newValue) {
                changedFields[field] = value;
                hasChanges = true;
                console.log(`[updateRSVPs] Field '${field}' changed for guest ${guestId}:`, {
                  oldValue: lastValue,
                  newValue: value
                });
              }
            }
          });

          if (hasChanges) {
            changedFieldsToSend[guestId] = changedFields;
          }
        });

        // Only return early if there are no actual changes to send
        if (Object.keys(changedFieldsToSend).length === 0) {
          console.log('[updateRSVPs] No changes to send, returning early');
          return; // No changes to send
        }
        
        console.log('[updateRSVPs] Sending changes:', changedFieldsToSend);

        setIsLoading(true);
        setError(null);

        // Construct request body with only changed fields
        const guestArray = Object.entries(changedFieldsToSend).map(([guestId, updates]) => ({
          guest_id: String(guestId),
          ...updates
        }));

        // Format request body
        const requestBody = {
          rsvp: guestArray
        };
        console.log('[updateRSVPs] Sending API request with body:', requestBody);

        // Make the API request
        const putOperation = put({
          apiName: 'events',
          path: `/invitation/${code}/guests/confirm`,
          options: {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: requestBody
          }
        });

        console.log('[updateRSVPs] API request sent, awaiting response...');
        const { body: putBody } = await putOperation.response;
        if (!putBody) {
          console.error('[updateRSVPs] Response is missing required body');
          throw new Error('Response is missing required body');
        }

        const putData = await putBody.json();
        console.log('[updateRSVPs] Received API response:', putData);
        
        if (!putData || !putData.rsvp) {
          console.error('[updateRSVPs] Invalid response format from server:', putData);
          throw new Error('Invalid response format from server');
        }

        // Process the response
        const rsvpArray = Array.isArray(putData) ? putData : 
                         (putData.rsvp && Array.isArray(putData.rsvp)) ? putData.rsvp : 
                         [];
        console.log('[updateRSVPs] Processed RSVP array:', rsvpArray);

        // Update lastConfirmedValues with the server response
        rsvpArray.forEach(response => {
          if (response.guest_id) {
            lastConfirmedValues.current[response.guest_id] = {
              ...lastConfirmedValues.current[response.guest_id],
              ...response,
              // TODO consider changing logic to ?? as it ignores falsy values
              attending: response.attending === true || response.attending === false 
                ? Boolean(response.attending)
                : undefined,
              dietaryRequirements: response.dietaryRequirements || '',
              additionalOptions: response.additionalOptions?.toString() || 'false',
              additionalNotes: response.additionalNotes || '',
              ageCategory: response.ageCategory || '',
              guestComments: response.guestComments || ''
            };
            console.log(`[updateRSVPs] Updated lastConfirmedValues for guest ${response.guest_id}:`, 
              lastConfirmedValues.current[response.guest_id]);
          }
        });

        // Process server responses into a map
        const serverResponseMap = rsvpArray.reduce((acc, response) => {
          if (response.guest_id) {
            acc[response.guest_id] = response;
          }
          return acc;
        }, {});
        console.log('[updateRSVPs] Server response map:', serverResponseMap);

        // Batch state updates
        flushSync(() => {
          console.log('[updateRSVPs] Starting batch state updates');
          
          // Update guestResponses state
          setGuestResponses(prevResponses => {
            const newResponses = { ...prevResponses };
            Object.entries(serverResponseMap).forEach(([guestId, serverResponse]) => {
              const attendingValue = serverResponse.attending === true || serverResponse.attending === false 
                ? Boolean(serverResponse.attending)
                : null;
              newResponses[guestId] = {
                ...serverResponse,
                attending: attendingValue,
                dietaryRequirements: serverResponse.dietaryRequirements || '',
                additionalOptions: serverResponse.additionalOptions?.toString() || 'false',
                additionalNotes: serverResponse.additionalNotes || '',
                ageCategory: serverResponse.ageCategory || '',
                guestComments: serverResponse.guestComments || ''
              };
            });
            console.log('[updateRSVPs] Updated guest responses:', newResponses);
            return newResponses;
          });

          // Update guestInputs to match server state
          setGuestInputs(prevInputs => {
            const newInputs = { ...prevInputs };
            Object.entries(serverResponseMap).forEach(([guestId, serverResponse]) => {
              newInputs[guestId] = {
                ...newInputs[guestId],
                attending: serverResponse.attending === true || serverResponse.attending === false 
                  ? Boolean(serverResponse.attending)
                  : null,
                dietaryRequirements: serverResponse.dietaryRequirements || '',
                additionalOptions: serverResponse.additionalOptions?.toString() || 'false',
                additionalNotes: serverResponse.additionalNotes || '',
                ageCategory: serverResponse.ageCategory || '',
                guestComments: serverResponse.guestComments || ''
              };
            });
            console.log('[updateRSVPs] Updated guest inputs:', newInputs);
            return newInputs;
          });

          // Clear changed fields for updated guests
          setChangedFields(prev => {
            const newChangedFields = { ...prev };
            Object.keys(serverResponseMap).forEach(guestId => {
              delete newChangedFields[guestId];
            });
            console.log('[updateRSVPs] Cleared changed fields:', newChangedFields);
            return newChangedFields;
          });

          // Reset button states
          setButtonStates(prev => {
            const newButtonStates = { ...prev };
            Object.keys(serverResponseMap).forEach(guestId => {
              newButtonStates[guestId] = {
                attending: false,
                'not-attending': false
              };
            });
            console.log('[updateRSVPs] Reset button states:', newButtonStates);
            return newButtonStates;
          });
        });

        // Clear the timestamps
        buttonStateTimestamps.current = {};
        console.log('[updateRSVPs] Cleared button timestamps');
      });

      // Wait for the update to complete
      console.log('[updateRSVPs] Waiting for update to complete...');
      await updatePromise;
      console.log('[updateRSVPs] Update completed successfully');

    } catch (error) {
      console.error('[updateRSVPs] Error updating guests:', {
        error,
        message: error.message,
        stack: error.stack
      });
      if (error.response?.status === 403) {
        setError('RSVP cutoff has passed. No further changes are allowed.');
      } else {
        setError(error.message || 'Failed to update guests. Please try again later.');
      }
      
      // Reset button states after error
      setButtonStates(prev => {
        const newButtonStates = { ...prev };
        Object.keys(updatedGuests).forEach(guestId => {
          newButtonStates[guestId] = {
            attending: false,
            'not-attending': false
          };
        });
        console.log('[updateRSVPs] Reset button states after error:', newButtonStates);
        return newButtonStates;
      });
    } finally {
      setIsLoading(false);
      
      // Clear this update from pending updates
      setPendingUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[updateId];
        console.log('[updateRSVPs] Cleared pending update:', {
          updateId,
          remainingUpdates: newUpdates
        });
        return newUpdates;
      });
    }
  }, [code, guestResponses, pendingUpdates]);

  // Create debounced update functions for each field type
  const debouncedTextUpdate = useCallback((guestId, fieldName, value, updateFn) => {
    console.log(`[debouncedTextUpdate] Starting update for guest ${guestId}:`, {
      fieldName,
      value,
      lastValue: lastConfirmedValues.current[guestId]?.[fieldName]
    });
    
    const currentValue = value;
    const lastValue = lastConfirmedValues.current[guestId]?.[fieldName];
    
    // Only update if the value has changed from the last confirmed value
    if (currentValue !== lastValue) {
      console.log(`[debouncedTextUpdate] Value changed for guest ${guestId}:`, {
        field: fieldName,
        oldValue: lastValue,
        newValue: currentValue
      });
      
      const updatedRsvp = {
        [guestId]: {
          guest_id: guestId,
          [fieldName]: currentValue
        }
      };
      console.log('[debouncedTextUpdate] Calling update function with:', updatedRsvp);
      
      updateFn(updatedRsvp).catch(error => {
        console.error(`[debouncedTextUpdate] Failed to update ${fieldName}:`, {
          error,
          message: error.message,
          stack: error.stack
        });
        // Revert the input value on error
        setGuestInputs(prev => ({
          ...prev,
          [guestId]: {
            ...prev[guestId],
            [fieldName]: lastValue || ''
          }
        }));
      });
    } else {
      console.log(`[debouncedTextUpdate] No change detected for guest ${guestId}, field ${fieldName}`);
    }
  }, []);

  // Create a memoized debounced update function for each guest
  const debouncedUpdateMap = useMemo(() => {
    const map = new Map();
    return {
      get: (guestId) => {
        if (!map.has(guestId)) {
          console.log(`[debouncedUpdateMap] Creating new debounced function for guest ${guestId}`);
          map.set(guestId, debounce((fieldName, value, updateFn) => {
            console.log(`[debouncedUpdateMap] Executing debounced update for guest ${guestId}:`, {
              fieldName,
              value
            });
            
            // Compare with last confirmed value
            const lastValue = lastConfirmedValues.current[guestId]?.[fieldName] || '';
            const newValue = value || '';
            
            if (lastValue !== newValue) {
              const updatedRsvp = {
                [guestId]: {
                  guest_id: guestId,
                  [fieldName]: value
                }
              };
              console.log(`[debouncedUpdateMap] Value changed, sending update for guest ${guestId}:`, {
                field: fieldName,
                oldValue: lastValue,
                newValue: value
              });
              updateFn(updatedRsvp).catch(error => {
                console.error(`[debouncedUpdateMap] Failed to update ${fieldName}:`, {
                  error,
                  message: error.message,
                  stack: error.stack
                });
                // Revert the input value on error
                setGuestInputs(prev => ({
                  ...prev,
                  [guestId]: {
                    ...prev[guestId],
                    [fieldName]: lastValue
                  }
                }));
              });
            } else {
              console.log(`[debouncedUpdateMap] No change detected for guest ${guestId}, field ${fieldName}`);
            }
          }, 5000));
        }
        return map.get(guestId);
      },
      cancel: (guestId) => {
        const fn = map.get(guestId);
        if (fn && typeof fn.cancel === 'function') {
          console.log(`[debouncedUpdateMap] Cancelling debounced updates for guest ${guestId}`);
          fn.cancel();
        }
      },
      cancelAll: () => {
        console.log('[debouncedUpdateMap] Cancelling all debounced updates');
        map.forEach((fn, guestId) => {
          if (fn && typeof fn.cancel === 'function') {
            console.log(`[debouncedUpdateMap] Cancelling updates for guest ${guestId}`);
            fn.cancel();
          }
        });
      }
    };
  }, []);

  // Cleanup all debounced functions on unmount
  useEffect(() => {
    return () => {
      console.log('[cleanup] Unmounting component, cleaning up debounced functions');
      debouncedUpdateMap.cancelAll();
    };
  }, [debouncedUpdateMap]);

  return (
    <div className="invitation">
      {isLoading && <LoadingSpinner message="Loading invitation details" />}
      
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {showPasswordForm ? (
        <div className="password-container" role="dialog" aria-labelledby="password-title">
          <h2 id="password-title" className="invitation-title">Protected Invitation</h2>
          <p className="invitation-subtitle">This invitation is password protected. Please enter the password to continue.</p>
          
          <form onSubmit={submitPassword} className="password-form">
            <div className="form-group">
              <label htmlFor="password" className="form">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="button button--primary"
            >
              {isLoading ? 'Verifying...' : 'Submit'}
            </button>
          </form>
        </div>
      ) : (
        <>
          {invitation && event && (
            <div className="invitation-container">
              <div className="invitation-decorative-element top-left"></div>
              <div className="invitation-decorative-element top-right"></div>
              <div className="invitation-decorative-element bottom-left"></div>
              <div className="invitation-decorative-element bottom-right"></div>
              
              <div className="invitation-header">
                <h3 className="invitation-dearguest">Dear {
                  guests.length === 1 ? 
                    (guests[0].shortName || guests[0].firstName) :
                  guests.length === 2 ?
                    `${guests[0].shortName || guests[0].firstName} & ${guests[1].shortName || guests[1].firstName}` :
                    guests.map((guest, index) => 
                      index === guests.length - 1 ?
                        ` and ${guest.shortName || guest.firstName}` :
                        `${guest.shortName || guest.firstName}${index < guests.length - 2 ? ', ' : ''}`
                    ).join('')
                }</h3>

                {event.abovename && (
                  <p className="invitation-abovename">{event.abovename}</p>
                )}
                
                <h1 className="invitation-name">
                    {event.name.split(' & ').map((part, index, array) => (
                        <React.Fragment key={index}>
                            {part}
                            {index < array.length - 1 && <span className="invitation-name-highlight">&</span>}
                        </React.Fragment>
                    ))}
                </h1>


                {event.heading && (
                  <p className="invitation-heading">
                    {event.heading}
                  </p>
                )}

                <div className="invitation-details">
                  <div className="invitation-detail-item">
                    <span className="invitation-detail-label date">Date:</span>
                    <span className="invitation-detail-value date">
                      {event.datetext || new Date(event.date).toLocaleDateString()}
                    </span>
                  </div>

                  {event.location && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label location">Location:</span>
                      <span className="invitation-detail-value location">
                        {event.location.split('\n').map((line, index) => (
                          <React.Fragment key={index}>
                              {line}
                              {index < event.location.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </span>
                      {safeHttpUrl(event.locationUrl) && (
                        <span className="invitation-detail-value location-url">
                          <a href={safeHttpUrl(event.locationUrl)} target="_blank" rel="noopener noreferrer">{event.locationUrl}</a>
                        </span>
                      )}
                      {safeHttpUrl(event.locationEmbed) && (
                        <span className="invitation-detail-value location-embed">
                          <iframe
                            src={safeHttpUrl(event.locationEmbed)}
                            title="Event location map"
                            width="400"
                            height="300"
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            sandbox="allow-scripts allow-popups allow-forms"
                          />
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="invitation-content">
                  {event.description && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label description">Description:</span>
                      <span className="invitation-detail-value description">
                        {renderMultilineText(event.description)}
                      </span>
                    </div>
                  )}

                  {event.instructions && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label instructions">Instructions:</span>
                      <span className="invitation-detail-value instructions">
                        {renderMultilineText(event.instructions)}
                      </span>
                    </div>
                  )}

                  {event.rsvpDetails && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label rsvp">RSVP</span>
                      <span className="invitation-detail-value rsvp">
                        {renderMultilineText(event.rsvpDetails)}
                      </span>
                    </div>
                  )}
                  
                  {event.rsvpCutoff && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label rsvp-cutoff">RSVP Cutoff:</span>
                      <span className="invitation-detail-value rsvp-cutoff">
                        {new Date(event.rsvpCutoff).toLocaleString()}
                      </span>
                    </div>
                  )}

                  {event.additionalInformation && (
                    <div className="invitation-detail-item">
                      <span className="invitation-detail-label additionalInformation">Additional information:</span>
                      <span className="invitation-detail-value additionalInformation">
                        {renderMultilineText(event.additionalInformation)}
                      </span>
                    </div>
                  )}
                </div>

                {rsvpCutoffPassed && (
                  <div className="cutoff-notice" style={{backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '4px', padding: '12px', margin: '16px 0'}}>
                    <p style={{color: '#d97706', fontWeight: '600', margin: 0}}>
                      ⚠️ The RSVP cutoff has passed. You can view your responses but cannot make changes.
                    </p>
                  </div>
                )}

                <div className="invitation-guest-list">
                  <h2 className="invitation-subtitle">Guest List</h2>
                  <div className="guest-count">Number of guests: {guests.length}</div>
                  
                  {guests.slice().sort((a, b) => {
                    const aIndex = invitation.guestIds.indexOf(a.id);
                    const bIndex = invitation.guestIds.indexOf(b.id);
                    return aIndex - bIndex;
                  }).map((guest, index) => {
                    const guestResponse = guestResponses[guest.id] || {};
                    return (
                      <div key={guest.id} className="guest-item">
                        <div className="guest-header">
                          <h3 className="guest-name">{guest.shortName || guest.firstName}</h3>
                          <span className={`guest-status ${
                            guestResponse.attending === true ? 'guest-status--attending' :
                            guestResponse.attending === false ? 'guest-status--declined' :
                            'guest-status--pending'
                          }`}>
                            {guestResponse.attending === true ? 'Attending' :
                             guestResponse.attending === false ? 'Not Attending' :
                             'Not Yet Responded'}
                          </span>
                        </div>
                        
                        <div className="guest-details">
                          <div className="guest-detail">
                            <label htmlFor={`dietary-${guest.id}`}>
                              <strong>Dietary Requirements:</strong>
                            </label>
                            <div className="input-container">
                              <input
                                id={`dietary-${guest.id}`}
                                data-testid={`dietary-${guest.id}`}
                                type="text"
                                value={guestInputs[guest.id]?.dietaryRequirements || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setGuestInputs(prev => ({
                                    ...prev,
                                    [guest.id]: {
                                      ...prev[guest.id],
                                      dietaryRequirements: newValue
                                    }
                                  }));
                                  trackFieldChange(guest.id, 'dietaryRequirements', newValue);
                                  setSavingState(guest.id, 'dietaryRequirements', 'saving');
                                  debouncedUpdateMap.get(guest.id)('dietaryRequirements', newValue, async (updatedRsvp) => {
                                    try {
                                      await updateRSVPs(updatedRsvp);
                                      setSavingState(guest.id, 'dietaryRequirements', 'success');
                                    } catch (error) {
                                      setSavingState(guest.id, 'dietaryRequirements', null);
                                      throw error;
                                    }
                                  });
                                }}
                                disabled={rsvpCutoffPassed}
                                readOnly={rsvpCutoffPassed}
                                onBlur={(e) => {
                                  const newValue = e.target.value;
                                  const lastValue = lastConfirmedValues.current[guest.id]?.dietaryRequirements;
                                  if (newValue !== lastValue) {
                                    // Cancel any pending debounced updates
                                    debouncedUpdateMap.cancel(guest.id);
                                    
                                    const updatedRsvp = {
                                      [guest.id]: {
                                        guest_id: guest.id,
                                        dietaryRequirements: newValue
                                      }
                                    };
                                    // Immediate update on blur
                                    setSavingState(guest.id, 'dietaryRequirements', 'saving');
                                    updateRSVPs(updatedRsvp)
                                      .then(() => setSavingState(guest.id, 'dietaryRequirements', 'success'))
                                      .catch(error => {
                                        console.error('Failed to update dietary requirements:', error);
                                        setSavingState(guest.id, 'dietaryRequirements', null);
                                        // Revert the input value on error
                                        setGuestInputs(prev => ({
                                          ...prev,
                                          [guest.id]: {
                                            ...prev[guest.id],
                                            dietaryRequirements: lastValue || ''
                                          }
                                        }));
                                      });
                                  }
                                }}
                                placeholder="Dietary requirements"
                                className="form-input"
                              />
                              <SavingIndicator 
                                state={savingStates[guest.id]?.dietaryRequirements}
                                guestId={guest.id}
                                field="dietaryRequirements"
                              />
                            </div>

                          </div>

                          <div className="guest-detail">
                            <label htmlFor={`age-${guest.id}`}>
                              <strong>Age Category:</strong>
                            </label>
                            <div className="input-container">
                              <select
                                id={`age-${guest.id}`}
                                data-testid={`age-${guest.id}`}
                                value={guestInputs[guest.id]?.ageCategory || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setGuestInputs(prev => ({
                                    ...prev,
                                    [guest.id]: {
                                      ...prev[guest.id],
                                      ageCategory: newValue
                                    }
                                  }));
                                  trackFieldChange(guest.id, 'ageCategory', newValue);
                                  setSavingState(guest.id, 'ageCategory', 'saving');
                                  // For dropdowns, update immediately
                                  const updatedRsvp = {
                                    [guest.id]: {
                                      guest_id: guest.id,
                                      ageCategory: newValue
                                    }
                                  };
                                  updateRSVPs(updatedRsvp)
                                    .then(() => setSavingState(guest.id, 'ageCategory', 'success'))
                                    .catch(error => {
                                      setSavingState(guest.id, 'ageCategory', null);
                                      console.error('Failed to update age category:', error);
                                      // Revert the dropdown state on error
                                      setGuestInputs(prev => ({
                                        ...prev,
                                        [guest.id]: {
                                          ...prev[guest.id],
                                          ageCategory: lastConfirmedValues.current[guest.id]?.ageCategory || ''
                                        }
                                      }));
                                    });
                                }}
                                disabled={rsvpCutoffPassed}
                                className="form-input"
                              >
                                <option value="">Please select...</option>
                                <option value="Adult">Adult</option>
                                <option value="Teenager">Teenager</option>
                                <option value="Child">Child</option>
                                <option value="Baby">Baby</option>
                              </select>
                              <SavingIndicator 
                                state={savingStates[guest.id]?.ageCategory}
                                guestId={guest.id}
                                field="ageCategory"
                              />
                            </div>
                          </div>

                          {event.additionalOptions && (
                          <div className="guest-detail">
                            <label htmlFor={`additionalOptions-${guest.id}`}>
                              <strong>{event.additionalOptions}</strong>
                            </label>
                            <div className="input-container">
                              <input type="checkbox"
                                id={`additionalOptions-${guest.id}`}
                                checked={guestInputs[guest.id]?.additionalOptions === 'true'}
                                onChange={(e) => {
                                  const newValue = e.target.checked.toString();
                                  setGuestInputs(prev => ({
                                    ...prev,
                                    [guest.id]: {
                                      ...prev[guest.id],
                                      additionalOptions: newValue
                                    }
                                  }));
                                  trackFieldChange(guest.id, 'additionalOptions', newValue);
                                  setSavingState(guest.id, 'additionalOptions', 'saving');
                                  // For checkboxes, update immediately
                                  const updatedRsvp = {
                                    [guest.id]: {
                                      guest_id: guest.id,
                                      additionalOptions: newValue
                                    }
                                  };
                                  updateRSVPs(updatedRsvp)
                                    .then(() => setSavingState(guest.id, 'additionalOptions', 'success'))
                                    .catch(error => {
                                      setSavingState(guest.id, 'additionalOptions', null);
                                      console.error('Failed to update additional options:', error);
                                      // Revert the checkbox state on error
                                      setGuestInputs(prev => ({
                                        ...prev,
                                        [guest.id]: {
                                          ...prev[guest.id],
                                          additionalOptions: !e.target.checked.toString()
                                        }
                                      }));
                                    });
                                }}
                                disabled={rsvpCutoffPassed}
                                className="form-input"
                              />
                              <SavingIndicator 
                                state={savingStates[guest.id]?.additionalOptions}
                                guestId={guest.id}
                                field="additionalOptions"
                              />
                            </div>
                          </div>
                          )}

                          <div className="guest-detail">
                            <label htmlFor={`notes-${guest.id}`}>
                              <strong>Additional Notes:</strong>
                            </label>
                            <div className="input-container">
                              <textarea
                                id={`notes-${guest.id}`}
                                data-testid={`notes-${guest.id}`}
                                value={guestInputs[guest.id]?.additionalNotes || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setGuestInputs(prev => ({
                                    ...prev,
                                    [guest.id]: {
                                      ...prev[guest.id],
                                      additionalNotes: newValue
                                    }
                                  }));
                                  trackFieldChange(guest.id, 'additionalNotes', newValue);
                                  setSavingState(guest.id, 'additionalNotes', 'saving');
                                  debouncedUpdateMap.get(guest.id)('additionalNotes', newValue, async (updatedRsvp) => {
                                    try {
                                      await updateRSVPs(updatedRsvp);
                                      setSavingState(guest.id, 'additionalNotes', 'success');
                                    } catch (error) {
                                      setSavingState(guest.id, 'additionalNotes', null);
                                      throw error;
                                    }
                                  });
                                }}
                                disabled={rsvpCutoffPassed}
                                readOnly={rsvpCutoffPassed}
                                onBlur={(e) => {
                                  const newValue = e.target.value;
                                  const lastValue = lastConfirmedValues.current[guest.id]?.additionalNotes;
                                  if (newValue !== lastValue) {
                                    // Cancel any pending debounced updates
                                    debouncedUpdateMap.cancel(guest.id);
                                    
                                    const updatedRsvp = {
                                      [guest.id]: {
                                        guest_id: guest.id,
                                        additionalNotes: newValue
                                      }
                                    };
                                    // Immediate update on blur
                                    setSavingState(guest.id, 'additionalNotes', 'saving');
                                    updateRSVPs(updatedRsvp)
                                      .then(() => setSavingState(guest.id, 'additionalNotes', 'success'))
                                      .catch(error => {
                                        setSavingState(guest.id, 'additionalNotes', null);
                                        console.error('Failed to update additional notes:', error);
                                        // Revert the input value on error
                                        setGuestInputs(prev => ({
                                          ...prev,
                                          [guest.id]: {
                                            ...prev[guest.id],
                                            additionalNotes: lastValue || ''
                                          }
                                        }));
                                      });
                                  }
                                }}
                                placeholder="Any additional notes"
                                className="form-input"
                                rows="3"
                              />
                              <SavingIndicator 
                                state={savingStates[guest.id]?.additionalNotes}
                                guestId={guest.id}
                                field="additionalNotes"
                              />
                            </div>
                          </div>

                          <div className="guest-detail">
                            <label htmlFor={`comments-${guest.id}`}>
                              <strong>Comments for Host:</strong>
                            </label>
                            <div className="input-container">
                              <textarea
                                id={`comments-${guest.id}`}
                                data-testid={`comments-${guest.id}`}
                                value={guestInputs[guest.id]?.guestComments || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setGuestInputs(prev => ({
                                    ...prev,
                                    [guest.id]: {
                                      ...prev[guest.id],
                                      guestComments: newValue
                                    }
                                  }));
                                  trackFieldChange(guest.id, 'guestComments', newValue);
                                  setSavingState(guest.id, 'guestComments', 'saving');
                                  debouncedUpdateMap.get(guest.id)('guestComments', newValue, async (updatedRsvp) => {
                                    try {
                                      await updateRSVPs(updatedRsvp);
                                      setSavingState(guest.id, 'guestComments', 'success');
                                    } catch (error) {
                                      setSavingState(guest.id, 'guestComments', null);
                                      throw error;
                                    }
                                  });
                                }}
                                disabled={rsvpCutoffPassed}
                                readOnly={rsvpCutoffPassed}
                                onBlur={(e) => {
                                  const newValue = e.target.value;
                                  const lastValue = lastConfirmedValues.current[guest.id]?.guestComments;
                                  if (newValue !== lastValue) {
                                    debouncedUpdateMap.cancel(guest.id);
                                    const updatedRsvp = {
                                      [guest.id]: {
                                        guest_id: guest.id,
                                        guestComments: newValue
                                      }
                                    };
                                    setSavingState(guest.id, 'guestComments', 'saving');
                                    updateRSVPs(updatedRsvp)
                                      .then(() => setSavingState(guest.id, 'guestComments', 'success'))
                                      .catch(error => {
                                        setSavingState(guest.id, 'guestComments', null);
                                        setGuestInputs(prev => ({
                                          ...prev,
                                          [guest.id]: {
                                            ...prev[guest.id],
                                            guestComments: lastValue || ''
                                          }
                                        }));
                                      });
                                  }
                                }}
                                placeholder="Share your thoughts or messages with the host"
                                className="form-input"
                                rows="3"
                              />
                              <SavingIndicator 
                                state={savingStates[guest.id]?.guestComments}
                                guestId={guest.id}
                                field="guestComments"
                              />
                            </div>
                          </div>
                          
                          <div className="guest-detail guest-actions">
                            <label><strong>
                              {guestResponse.attending !== true && guestResponse.attending !== false ? 
                                "Please respond to this invitation" : 
                                "Change attendance status"}
                            </strong></label>
                            <button
                              onClick={async () => {
                                const guestId = guest.id;
                                buttonStateTimestamps.current[guestId] = Date.now();
                                
                                // Set button state immediately
                                setButtonStates(prev => ({
                                  ...prev,
                                  [guestId]: {
                                    attending: true,
                                    'not-attending': false
                                  }
                                }));
                                
                                // Track the change
                                trackFieldChange(guestId, 'attending', true);
                                
                                // Cancel any pending debounced updates
                                debouncedUpdateMap.cancel(guestId);
     
                                // Create the update payload with only changed field
                                const updatedRsvp = {
                                  [guestId]: {
                                    guest_id: guestId,
                                    attending: true
                                  }
                                };
                                
                                try {
                                  setSavingState(guestId, 'attending-yes', 'saving');
                                  // Immediate update
                                  await updateRSVPs(updatedRsvp);
                                  setSavingState(guestId, 'attending-yes', 'success');
                                } catch (error) {
                                  console.error('RSVP update failed:', error);
                                  setSavingState(guestId, 'attending-yes', null);
                                  // Reset button state on error
                                  setButtonStates(prev => ({
                                    ...prev,
                                    [guestId]: {
                                      attending: false,
                                      'not-attending': false
                                    }
                                  }));
                                }
                              }}
                              className={`button button--primary ${buttonStates[guest.id]?.attending ? 'button--pulsing' : ''} ${guestResponse.attending === false && !buttonStates[guest.id]?.attending ? 'button--notselected' : ''}`}
                              disabled={rsvpCutoffPassed || (guestResponse.attending === true && !buttonStates[guest.id]?.attending)}
                              aria-label={`Mark ${guest.shortName || guest.firstName} as attending`}
                            >
                              {guestResponse.attending === true ? 'Attending' : 'I Will Attend'}
                              <SavingIndicator 
                                state={savingStates[guest.id]?.['attending-yes']}
                                guestId={guest.id}
                                field="attending-yes"
                              />
                            </button>
                            <button
                              onClick={async () => {
                                const guestId = guest.id;
                                buttonStateTimestamps.current[guestId] = Date.now();
                                
                                // Set button state immediately
                                setButtonStates(prev => ({
                                  ...prev,
                                  [guestId]: {
                                    attending: false,
                                    'not-attending': true
                                  }
                                }));
                                
                                // Track the change
                                trackFieldChange(guestId, 'attending', false);
                                
                                // Cancel any pending debounced updates
                                debouncedUpdateMap.cancel(guestId);
                                
                                // Create the update payload with only changed field
                                const updatedRsvp = {
                                  [guestId]: {
                                    guest_id: guestId,
                                    attending: false
                                  }
                                };
                                
                                try {
                                  setSavingState(guestId, 'attending-no', 'saving');
                                  // Immediate update
                                  await updateRSVPs(updatedRsvp);
                                  setSavingState(guestId, 'attending-no', 'success');
                                } catch (error) {
                                  console.error('RSVP update failed:', error);
                                  setSavingState(guestId, 'attending-no', null);
                                  // Reset button state on error
                                  setButtonStates(prev => ({
                                    ...prev,
                                    [guestId]: {
                                      attending: false,
                                      'not-attending': false
                                    }
                                  }));
                                }
                              }}
                              className={`button button--danger ${buttonStates[guest.id]?.['not-attending'] ? 'button--pulsing' : ''} ${guestResponse.attending === true && !buttonStates[guest.id]?.['not-attending'] ? 'button--notselected' : ''}`}
                              disabled={rsvpCutoffPassed || (guestResponse.attending === false && !buttonStates[guest.id]?.['not-attending'])}
                              aria-label={`Mark ${guest.shortName || guest.firstName} as not attending`}
                            >
                              {guestResponse.attending === false ? 'Not Attending' : 'I Cannot Attend'}
                              <SavingIndicator 
                                state={savingStates[guest.id]?.['attending-no']}
                                guestId={guest.id}
                                field="attending-no"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OpenInvitationScreen;
